import { getReceptionSettings } from "@/core/admin/establishment-store";
import { listAdmissions } from "@/core/admissions/admission-store";
import {
  createAndIssueActBillingDocument,
  getBillingDocumentByAdmission,
  isAdmissionConsultationPaid,
} from "@/core/billing/billing-documents/billing-documents-store";
import { getConsultationById } from "@/core/clinical/clinical-store";
import { patchCoverageCase } from "@/core/insurance/coverage-case-store";
import type { ImagingExam } from "@/core/imaging/types";
import type { LabOrder } from "@/core/laboratory/types";
import { getPatientById } from "@/core/patients/patient-store";
import { hasThirdPartyCoverage } from "@/core/patients/types";
import { findQueueTicketByConsultation } from "@/core/queue/service-queue-store";

const DEFAULT_LAB_EXAM_FEE = 25_000;
const DEFAULT_IMAGING_EXAM_FEE = 45_000;

function shouldBillInsurerForAct(
  patientId: string,
  insurerApproval?: LabOrder["insurerApproval"] | ImagingExam["insurerApproval"] | null,
): boolean {
  const patient = getPatientById(patientId);
  if (!patient || !hasThirdPartyCoverage(patient)) return false;
  if (insurerApproval === "rejected" || insurerApproval === "self_pay") return false;
  return true;
}

function linkCoverageCaseBilling(coverageCaseId: string | null | undefined, documentId: string): void {
  if (!coverageCaseId) return;
  patchCoverageCase(coverageCaseId, { billingDocumentId: documentId });
}

function resolveAdmissionForConsultation(
  consultationId: string,
  patientId: string,
): { admissionId: string | null; serviceLabel: string } {
  const ticket = findQueueTicketByConsultation(consultationId);
  if (ticket?.admissionId) {
    return { admissionId: ticket.admissionId, serviceLabel: ticket.unitName || "Médecine générale" };
  }

  const todayAdmissions = listAdmissions()
    .filter((a) => a.patientId === patientId)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const admission = todayAdmissions[0];
  if (!admission) {
    return { admissionId: null, serviceLabel: "Médecine générale" };
  }

  const dest = admission.destinations[0] ?? admission.destination;
  return {
    admissionId: admission.id,
    serviceLabel: dest?.trim() || "Médecine générale",
  };
}

/** Consultation clôturée → facture assureur ou proforma patient. */
export async function autoBillConsultationOnClose(consultationId: string): Promise<void> {
  const consultation = getConsultationById(consultationId);
  if (!consultation || consultation.status !== "closed") return;

  const { admissionId, serviceLabel } = resolveAdmissionForConsultation(
    consultationId,
    consultation.patientId,
  );

  if (admissionId) {
    const admissionDoc = getBillingDocumentByAdmission(admissionId);
    if (admissionDoc && (admissionDoc.status === "paid" || admissionDoc.status === "issued")) {
      return;
    }
    if (isAdmissionConsultationPaid(admissionId)) return;
  }

  const fee = getReceptionSettings().generalConsultationFee;
  const label = consultation.acts?.trim() || serviceLabel;

  const doc = await createAndIssueActBillingDocument({
    patientId: consultation.patientId,
    source: "consultation",
    serviceLabel: label,
    unitPrice: fee,
    linePrefix: "Consultation",
    admissionId,
    consultationId,
    forcePatientPayer: !shouldBillInsurerForAct(consultation.patientId),
  });

  if (doc && admissionId && doc.payerType !== "patient") {
    window.dispatchEvent(
      new CustomEvent("hospitol:consultation-billed", {
        detail: { consultationId, documentId: doc.id, admissionId },
      }),
    );
  }
}

/** Résultat labo validé → facture assureur ou proforma patient. */
export async function autoBillLabOrderOnComplete(order: LabOrder): Promise<void> {
  if (order.status !== "completed") return;

  const billInsurer = shouldBillInsurerForAct(order.patientId, order.insurerApproval);

  const doc = await createAndIssueActBillingDocument({
    patientId: order.patientId,
    source: "laboratory",
    serviceLabel: order.testName,
    unitPrice: DEFAULT_LAB_EXAM_FEE,
    linePrefix: "Laboratoire",
    labOrderId: order.id,
    consultationId: order.consultationId,
    coverageCaseId: order.coverageCaseId,
    forcePatientPayer: !billInsurer,
  });

  if (doc) {
    linkCoverageCaseBilling(order.coverageCaseId, doc.id);
  }
}

/** Compte-rendu imagerie validé → facture assureur ou proforma patient. */
export async function autoBillImagingExamOnComplete(exam: ImagingExam): Promise<void> {
  if (exam.status !== "completed") return;

  const label = exam.bodyPart ? `${exam.examType} — ${exam.bodyPart}` : exam.examType;
  const billInsurer = shouldBillInsurerForAct(exam.patientId, exam.insurerApproval);

  const doc = await createAndIssueActBillingDocument({
    patientId: exam.patientId,
    source: "imaging",
    serviceLabel: label,
    unitPrice: DEFAULT_IMAGING_EXAM_FEE,
    linePrefix: "Imagerie",
    imagingExamId: exam.id,
    consultationId: exam.consultationId,
    coverageCaseId: exam.coverageCaseId,
    forcePatientPayer: !billInsurer,
  });

  if (doc) {
    linkCoverageCaseBilling(exam.coverageCaseId, doc.id);
  }
}

function onConsultationClosed(event: Event): void {
  const detail = (event as CustomEvent<{ consultationId: string }>).detail;
  if (!detail?.consultationId) return;
  void autoBillConsultationOnClose(detail.consultationId);
}

/** Enregistre l'écouteur global (import side-effect depuis main.tsx). */
export function registerActCompletionBilling(): void {
  window.addEventListener("hospitol:consultation-closed", onConsultationClosed);
}

registerActCompletionBilling();
